import { useState, useRef, useEffect } from 'react';
import {
  Box,
  Text,
  Button,
  Drawer,
  Portal,
  HStack,
  Flex,
  CloseButton,
} from '@chakra-ui/react';
import { useShareTargetContext } from '@/contexts/ShareTargetContext';
import { useImportSheet } from '@/contexts/ImportSheetContext';
import { RecipeClient } from '@/lib/recipeClient';
import { toaster } from '@/lib/toaster';

function truncateUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const display = parsed.hostname + parsed.pathname;
    return display.length > 48 ? display.slice(0, 45) + '…' : display;
  } catch {
    return url.length > 48 ? url.slice(0, 45) + '…' : url;
  }
}

export default function ShareTargetPrompt() {
  const { pendingShareUrl, clearPendingShareUrl } = useShareTargetContext();
  const { startPolling, isPolling } = useImportSheet();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const pollingStartedRef = useRef(false);

  const isOpen = !!pendingShareUrl;
  const isImporting = isSubmitting || isPolling;

  // Close the drawer once polling finishes (navigation is handled by ImportSheetContext.onSuccess).
  useEffect(() => {
    if (pollingStartedRef.current && !isPolling) {
      pollingStartedRef.current = false;
      clearPendingShareUrl();
    }
  }, [isPolling, clearPendingShareUrl]);

  const handleImport = async () => {
    if (!pendingShareUrl) return;
    setIsSubmitting(true);
    try {
      const response = await RecipeClient.importFromUrl({ url: pendingShareUrl });
      pollingStartedRef.current = true;
      startPolling(response.jobId);
      setIsSubmitting(false); // polling takes over the loading state
    } catch {
      setIsSubmitting(false);
      toaster.create({
        title: 'Could not start import',
        description: 'Please try importing the link manually.',
        type: 'error',
      });
    }
  };

  const handleCancel = () => {
    clearPendingShareUrl();
  };

  return (
    <Drawer.Root
      open={isOpen}
      onOpenChange={(e) => { if (!e.open) handleCancel(); }}
      placement="bottom"
    >
      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content
            bg="bg.canvas"
            roundedTop="2xl"
            overflow="hidden"
          >
            <Drawer.Header
              bg="bg.canvas"
              borderBottom="none"
              px={5}
              pt={3}
              pb={0}
              flexDirection="column"
              alignItems="flex-start"
              gap={0}
            >
              {/* Drag handle */}
              <Box display="flex" justifyContent="center" mb={3} w="100%">
                <Box w="36px" h="4px" borderRadius="2px" bg="border.muted" />
              </Box>

              {/* Title + close */}
              <Flex justify="space-between" align="center" w="100%" mb={2}>
                <Text fontFamily="heading" fontWeight="800" fontSize="16px" color="text.primary">
                  Import this to my collection?
                </Text>
                <Drawer.CloseTrigger asChild>
                  <CloseButton size="sm" color="text.muted" disabled={isImporting} />
                </Drawer.CloseTrigger>
              </Flex>
            </Drawer.Header>

            <Drawer.Body px={5} pt={2} pb={6}>
              <Text
                fontFamily="body"
                fontSize="13px"
                color="text.muted"
                mb={5}
                truncate
              >
                {pendingShareUrl ? truncateUrl(pendingShareUrl) : ''}
              </Text>

              <HStack gap={3}>
                <Button
                  variant="outline"
                  flex={1}
                  borderColor="border.muted"
                  color="text.primary"
                  fontFamily="body"
                  onClick={handleCancel}
                  disabled={isImporting}
                >
                  Cancel
                </Button>
                <Button
                  flex={1}
                  bg="accent.emphasis"
                  color="white"
                  _hover={{ bg: 'accent.emphasisHover' }}
                  fontFamily="body"
                  onClick={handleImport}
                  loading={isImporting}
                  loadingText="Importing…"
                  disabled={isImporting}
                >
                  Import
                </Button>
              </HStack>
            </Drawer.Body>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
}
